import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useProfile } from '@/hooks';
import { Avatar } from '@/components';
import {
  useClanControllerFindMembersQuery,
  useClanControllerFindOneQuery,
} from '@/store/services/MidleNestApi.service';

function NotInGuild() {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col grow w-full items-center justify-center'>
      <div className='w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center'>
        <i className='text-3xl icon-group' />
      </div>

      <div className='text-center mt-4'>
        <h3 className='font-medium text-md text-slate-800'>
          {t('You are not a member of any clan')}
        </h3>
        <p className='font-normal text-sm text-slate-500'>
          {t('You can discover the strongest clans by clicking the button below.')}
        </p>
      </div>

      <Link
        to='/leaderboards/clans'
        className='px-8 py-2 font-medium bg-transparent text-blue-500 border border-blue-500 rounded-lg text-center mt-8'
      >
        {t('Go to Clan Leaderboard')}
        <span className='icon-share text-xl ml-2' />
      </Link>
    </div>
  );
}

export default function MyClan() {
  const {
    profile: { clan },
  } = useProfile();
  const isInClan = !!clan;

  const { data: members } = useClanControllerFindMembersQuery(
    { page: 1, limit: 4, id: clan?.id || '' },
    { skip: !isInClan },
  );

  const { data: clanDetails } = useClanControllerFindOneQuery(clan?.id || '', { skip: !isInClan });

  const { t } = useTranslation();

  return (
    <div className='self-stretch p-3 lg:p-6 flex flex-col basis-1/2 max-w-1/2 bg-white dark:bg-[#242424] rounded-3xl shadow-sm space-y-4'>
      <h3 className='text-xl leading-7 font-bold dark:text-white'>{t('My Clan')}</h3>

      {!isInClan ? (
        <NotInGuild />
      ) : (
        <>
          <div className='p-4 w-full flex justify-between items-center flex-nowrap  rounded-xl bg-gray-100 dark:bg-black/20'>
            <div className='flex gap-x-2 items-center text-sm text-gray-800 dark:text-white'>
              <Avatar name={clan?.name} src={clan?.avatar?.src} className='w-10 h-10' />
              <span>{clan?.name}</span>
            </div>
            <div className='ml-4 flex flex-wrap gap-2 md:gap-4'>
              <p className='text-sm text-gray-500 dark:text-white/70'>
                {t('Members')}:{' '}
                <span className='font-bold text-gray-800 dark:text-white'>
                  {clanDetails?.membersCount}
                </span>
              </p>
              <p className='text-sm text-gray-500 dark:text-white/70'>
                {t('Points')}:{' '}
                <span className='font-bold text-gray-800 dark:text-white'>
                  {clanDetails?.leaderboardActivity?.totalXp}
                </span>
              </p>
              <p className='text-sm text-gray-500 dark:text-white/70'>
                {t('Activity')}:{' '}
                <span className='font-bold text-gray-800 dark:text-white'>
                  {clanDetails?.leaderboardActivity?.activityRating}
                </span>
              </p>
            </div>
          </div>

          <div className='w-full grow'>
            <table className='w-full table-auto dark:text-white'>
              <thead>
                <tr className='text-sm sm:text-base text-center font-semibold'>
                  <td />
                  <td className='py-2 text-left'>{t('Member')}</td>
                  <td className='py-2 hidden sm:table-cell'>{t('Role')}</td>
                  <td className='py-2 hidden sm:table-cell'>{t('Avg. Points per Day')}</td>
                  <td className='py-2'>{t('Points')}</td>
                </tr>
              </thead>
              <tbody className='max-h-64 overflow-y-auto'>
                {members?.list &&
                  members.list.map((i, k) => (
                    <tr key={k} className='text-sm sm:text-base text-center'>
                      <td className=''>{k + 1}</td>
                      <td className='py-3 flex gap-x-2 items-center'>
                        <Avatar
                          name={i?.profile?.user?.username as string}
                          src={i?.profile?.avatar?.src}
                          className='w-10 h-10'
                        />
                        <span>{i?.profile?.user?.username}</span>
                      </td>
                      <td className='py-3 hidden sm:table-cell'>{i?.role}</td>
                      <td className='py-3 hidden sm:table-cell'>
                        {Math.round(
                          i?.leaderboardActivity?.totalXp / (i?.leaderboardActivity?.hours / 24),
                        )}
                      </td>
                      <td className='py-3'>{i?.leaderboardActivity?.totalXp}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className='w-full flex justify-end'>
            <Link
              to={'/clans/' + clan?.id}
              className='text-blue-500 dark:text-[#FFDA68] text-sm font-medium flex items-center'
            >
              View My Clan <i className='icon-angle-right text-xl' />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
